import { CodingSystem } from '@lifeomic/fhir-constants';
import { CustomMessages } from '../Components/SurveyBuilder/types';

export default {
  /** Display elements will be rendered as markdown */
  markdownDisplay: 'http://lifeomic.com/fhir/validation/markdown-display',
  /** Display elements will display as the specified typography variant */
  displayTypographyVariant:
    'http://lifeomic.com/fhir/validation/display-typography-variant',
  /** The group items will get rendered as a radio button table */
  tableGroup: 'http://lifeomic.com/fhir/table-group',
  /** Number cannot be negative */
  nonNegative: 'http://lifeomic.com/fhir/validation/non-negative',
  /** Item will render with additional left padding */
  indentation: 'http://lifeomic.com/fhir/indentation',
  /** Item will render with condensed margins */
  denseMargins: 'http://lifeomic.com/fhir/dense-margins',
  /** Item will render with condensed margins */
  sameLine: 'http://lifeomic.com/fhir/same-line',
  /** A suffix to display on option items */
  scaleLabel: 'http://lifeomic.com/fhir/scale-label',
  /** The observation code the resulting response will recieve when generating fhir observations */
  itemCode: 'http://lifeomic.com/fhir/questionnaire/item',
  /** The type of survey builder question this item represents */
  builderQuestionType: 'http://lifeomic.com/fhir/survey-builder-question-type',
  /** Indicates this page will not let you come back to it after submission is complete */
  checkpointPage: 'http://lifeomic.com/fhir/checkpoint-page',
  /** Generates a value set system ID */
  getSurveyValueSetSystem: (surveyId: string, valueSetId: string) =>
    `http://lifeomic.com/fhir/survey-value-sets/${surveyId}/${valueSetId}`,
  /** Specify a custom enablement comparator */
  customComparator: 'http://lifeomic.com/fhir/enablement/comparator',
  /** How many decimal places are permitted on a decimal value */
  valuePrecision: 'http://lifeomic.com/fhir/value-precision',
  /** The questionnaire schema does not natively support a way to pre-configure the quantity unit.  This extension's value quantity will specify the reference quantity */
  referenceQuantity: 'http://lifeomic.com/fhir/reference-quantity',
  /** Existence of extension dictates that the value of this survey item is finalized and further updates to it are forbidden */
  updatesForbidden: 'http://lifeomic.com/fhir/updates-forbidden',
  /** Enablement rules are ANDed by default, adding this extension with OR them */
  orEnablementRules: 'http://lifeomic.com/fhir/or-enablement-rules',
  /** Determines if this survey should only be used internally (via subject viewer for ex) or can be sent to subjects directly */
  internalSurvey: 'http://lifeomic.com/fhir/internal-survey',
  /** Fhir does not support a "not started" status.  To mitigate this, this extension is added when not yet started */
  surveyNotStarted:
    'http://lifeomic.com/fhir/questionnaireresponse/survey-not-started',
  /** Extension used for tracking a surveys response's last visited page */
  lastVisitedPage:
    'http://lifeomic.com/fhir/questionnaireresponse/last-visited-page',
  /** Extension used for validating the minimal allowed numeric value */
  minNumericBounds: 'http://lifeomic.com/fhir/validation/min-numeric-bounds',
  /** Extension used for validating the maximum allowed numeric value */
  maxNumericBounds: 'http://lifeomic.com/fhir/validation/max-numeric-bounds',
  /** Extension used for validating the minimal allowed selection count for a multi-select question */
  minSelectionCount: 'http://lifeomic.com/fhir/validation/min-selection-count',
  /** Extension used for validating the maximum allowed selection count for a multi-select question */
  maxSelectionCount: 'http://lifeomic.com/fhir/validation/max-selection-count',
  /** Extension used for validating the minimal allowed characters */
  minCharCount: 'http://lifeomic.com/fhir/validation/min-char-count',
  /** Extension used for validating the maximum allowed characters */
  maxCharCount: 'http://lifeomic.com/fhir/validation/max-char-count',
  /** Survey-level opt-in for rendering repeating groups (repeats on group items) as instance cards */
  enableRepeatingGroups: 'http://lifeomic.com/fhir/enable-repeating-groups',
  /** Minimum number of instances of a repeating group (standard fhir extension) */
  minOccurs: 'http://hl7.org/fhir/StructureDefinition/questionnaire-minOccurs',
  /** Maximum number of instances of a repeating group (standard fhir extension) */
  maxOccurs: 'http://hl7.org/fhir/StructureDefinition/questionnaire-maxOccurs',
  /** Designates that a selection control should be rendered as a radio button group */
  radioButtonGroup: 'http://lifeomic.com/fhir/radio-button-group',
  /** Represents the primary or root survey ID.  the primary survey ID is shared amongst different versions of the survey */
  primarySurveyId: 'http://lifeomic.com/fhir/primary-survey-id',
  /** Represents the lifeology course url */
  lifeologyCourseUrl: 'http://lifeomic.com/fhir/lifeology-course-url',
  /** The numeric value associated with a survey list option*/
  surveyListOptionNumericValue:
    'http://lifeomic.com/fhir/survey-list-option-numeric-value',
  /** Sum the descendent items in the survey*/
  sumScoreSection: 'http://lifeomic.com/fhir/survey-score-sum',
  /** Average the descendent items in the survey*/
  avgScoreSection: 'http://lifeomic.com/fhir/survey-score-avg',
  /** Omit target and descendent items from scoring*/
  omitScore: 'http://lifeomic.com/fhir/omit-score',
  /** Show the result score to the users */
  showScore: 'http://lifeomic.com/fhir/survey-show-score',
  /** A survey score label description markdown value */
  surveyScoreLabelMarkdownValue:
    'http://lifeomic.com/fhir/survey-score-label-markdown-value',
  /** A survey score label numeric value */
  surveyScoreLabelNumericValue:
    'http://lifeomic.com/fhir/survey-score-label-numeric-value',
  /** Show the result score to the users */
  enableRetakeSurvey: 'http://lifeomic.com/fhir/survey-enable-retake',
  /** The image ID (not file service ID) of the image question type */
  imageId: 'http://lifeomic.com/fhir/image-id',
  customMessage: (messageType: CustomMessages) =>
    `http://lifeomic.com/fhir/survey/custom-message/${messageType}`,
  /** The custom unit label to override the UCUM generated label */
  unitLabel: 'http://lifeomic.com/fhir/unit-label',
  /** If present on a matrix table question, will force the question set to render as a table even on mobile */
  forceMatrixTable: 'http://lifeomic.com/fhir/force-matrix-table',
  autopopulateValues: {
    /**
     * What resource type will be queried for the populate value
     */
    resourceType: 'http://lifeomic.com/fhir/survey-auto-populate/resource-type',
    /**
     * Query params that match on field = value.  The value string is structured in the format
     * <KEY 1>__:__<VALUE 1>__|__<KEY 2>__:__<VALUE 2>...
     */
    queryParams: 'http://lifeomic.com/fhir/survey-auto-populate/query-params',
    /**
     * A JMESPath selector to be used to extract the value from the raw result
     */
    valueSelector:
      'http://lifeomic.com/fhir/survey-auto-populate/value-selector',
  },
  /** Dynamic initial value configuration */
  initialValueDynamic: {
    /** Mode for computing dynamic defaults (e.g., responseDate) */
    mode: 'http://lifeomic.com/fhir/initial-value-dynamic/mode',
  },
  evidenceLevelValues: 'http://lifeomic.com/fhir/evidence-level-values',
  scoreLabel: 'http://lifeomic.com/fhir/score-label',
  allowsResponseResubmission:
    'http://lifeomic.com/fhir/allows-response-resubmission',
  /** When present, the live template preview pane is hidden while filling the survey. Absent = show (default). */
  hideTemplatePreview: 'http://lifeomic.com/fhir/hide-template-preview',
  /** Render a `text` item as a plain multiline textarea instead of the Markdown editor */
  plainTextArea: 'http://lifeomic.com/fhir/plain-text-area',
  /**
   * Render a group as a collapsible section with a toggleable header.
   * Presence of the extension is enough. valueBoolean: false starts collapsed;
   * missing or true starts expanded (the default).
   */
  collapsibleSection: 'http://lifeomic.com/fhir/collapsible-section',
  /**
   * Render a group as a compact identity confirm strip. Child questions stay
   * in the response (autopopulate, templates) but are not shown as inputs.
   */
  confirmStrip: 'http://lifeomic.com/fhir/confirm-strip',
  /**
   * Marks a child of a table-group as a row detail: instead of rendering as a
   * radio row, it renders inside the trigger row its first enableWhen
   * condition references (e.g. a "kit #" field under a specimen's Yes/No row)
   */
  tableRowDetail: 'http://lifeomic.com/fhir/table-row-detail',
  /**
   * Caps the rendered width of a question's input (valueString: xs|sm|md|lg).
   * Opt-in per question; absent keeps the current full-width behavior.
   */
  fieldWidth: 'http://lifeomic.com/fhir/field-width',
  /**
   * On a table-group item, renders Yes/No radio cells in the desktop matrix
   * as check (Yes) / X (No) icons for fast checklist scanning. Only applies
   * to options whose value is exactly "Yes" or "No"; other options (and the
   * mobile list variant) keep standard radios. Absent = radios (the default).
   */
  tableCheckIcons: 'http://lifeomic.com/fhir/table-check-icons',
  /**
   * Survey-level (questionnaire root) opt-in that caps the rendered form
   * width to a compact, centered column for tablet/kiosk data entry.
   * Absent = today's full-width behavior (the default).
   */
  compactLayout: 'http://lifeomic.com/fhir/compact-layout',
  /**
   * Hides a question's visible label while keeping it as the input's
   * accessible name. Intended for same-line unit pairs (Height feet/inches),
   * where repeating the label reads as a duplicate. Absent = visible label.
   */
  hideLabel: 'http://lifeomic.com/fhir/hide-label',
  vendorId: CodingSystem.VENDOR_ID,
};
